import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Volume2, User, UserCheck, PlayCircle, StopCircle, Loader2 } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';
import { useVoices } from '@/hooks/useVoices';
import { useVoicePreview } from '@/hooks/useVoicePreview';

interface VoiceGenderSelectionProps {
  formData: Partial<QuestionnaireData>;
  setFormData: (updates: Partial<QuestionnaireData>) => void;
}

const VoiceGenderSelection: React.FC<VoiceGenderSelectionProps> = ({ formData, setFormData }) => {
  const { getVoicesByGender, isLoading: voicesLoading } = useVoices();
  const { previewingVoice, loadingVoice, playVoicePreview } = useVoicePreview();
  const availableVoices = formData.voiceGender ? getVoicesByGender(formData.voiceGender) : [];

  return (
    <div className="space-y-6">
      <Card className="border-2 border-purple-200 bg-purple-50/50">
        <CardHeader className="text-center pb-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Volume2 className="h-6 w-6 text-purple-600" />
            <CardTitle className="text-xl text-purple-800">Choose Your Voice</CardTitle>
          </div>
          <p className="text-purple-600">First select the voice gender, then choose your preferred speaker</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <Label className="text-base font-semibold text-purple-700">Voice Gender</Label>
            <RadioGroup
              value={formData.voiceGender}
              onValueChange={(value) => {
                console.log('Setting voiceGender to:', value);
                // Auto-select the default voice for the chosen gender
                const defaultVoiceId = `${value}_default`;
                console.log('Auto-selecting default voice:', defaultVoiceId);
                setFormData({
                  voiceGender: value,
                  specific_voice_id: defaultVoiceId // Auto-select default voice for gender
                });
              }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="flex items-center space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer">
                <RadioGroupItem value="female" id="female" />
                <Label htmlFor="female" className="text-lg font-medium cursor-pointer flex-1 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Female Voice
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer">
                <RadioGroupItem value="male" id="male" />
                <Label htmlFor="male" className="text-lg font-medium cursor-pointer flex-1 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Male Voice
                </Label>
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>

      {formData.voiceGender && (
        <Card className="border-2 border-purple-200 bg-purple-50/50">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2 mb-2">
              <UserCheck className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg text-purple-800">
                Select {formData.voiceGender === 'male' ? 'Male' : 'Female'} Voice
              </CardTitle>
            </div>
            <p className="text-purple-600">Choose the specific voice that resonates with you</p>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={formData.specific_voice_id}
              onValueChange={(value) => {
                console.log('Setting specific_voice_id to:', value);
                setFormData({ specific_voice_id: value });
              }}
              className="space-y-3"
            >
              {availableVoices.map((voice) => (
                <div
                  key={voice.id}
                  className="flex items-center justify-between space-x-3 p-4 rounded-lg border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all cursor-pointer"
                >
                  <div className="flex items-start space-x-3 flex-1">
                    <RadioGroupItem value={voice.id} id={voice.id} className="mt-1" />
                    <div className="flex-1">
                      <Label htmlFor={voice.id} className="text-lg font-medium cursor-pointer block mb-1">
                        {voice.name}
                      </Label>
                      <p className="text-sm text-gray-600 cursor-pointer">{voice.description}</p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => playVoicePreview(voice.id)}
                    className="p-2 hover:bg-purple-100"
                    disabled={loadingVoice === voice.id}
                    title={
                      loadingVoice === voice.id ? "Loading preview..." :
                      previewingVoice === voice.id ? "Stop preview" :
                      "Preview voice"
                    }
                  >
                    {loadingVoice === voice.id ? (
                      <Loader2 className="h-4 w-4 text-purple-600 animate-spin" />
                    ) : previewingVoice === voice.id ? (
                      <StopCircle className="h-4 w-4 text-purple-600" />
                    ) : (
                      <PlayCircle className="h-4 w-4 text-purple-600" />
                    )}
                  </Button>
                </div>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VoiceGenderSelection;
